import {
  Avatar,
  Badge,
  Button,
  Drawer,
  EmptyState,
  Table,
  useDrawer,
} from '@iveco-ti/iveco-ds'
import { useTranslation } from 'react-i18next'

import { ApplicationModel } from '@/common/models/application'
import { colorAvatarByName } from '@/common/utils/colorAvatarByName'
import { countryCodeToFlag } from '@/common/utils/countryCodeToFlag'
import { translateApplicationAccountUserRole } from '@/common/utils/translateApplicationAccountUserRole'
import { UserApplicationAccountUserPaginated } from '../hooks/usePaginateUserApplicationAccountUser'
import { useSelectedUserApplicationAccountUserStore } from '../stores/useSelectedUserApplicationAccountUserStore'

export function DrawerViewApplicationAccountUser() {
  const { t, i18n } = useTranslation(['sso', 'common'])
  const { userAccess } = useSelectedUserApplicationAccountUserStore()
  const drawerViewApplicationAccountUser = useDrawer(
    'drawer-view-application-account-user',
  )

  const regionName = new Intl.DisplayNames([i18n.language], {
    type: 'region',
  })

  const TABLE_COLUMNS = [
    {
      label: t(
        'sso:access.drawer_view_application_account_user.table_head.column_first.label',
      ),
    },
    {
      label: t(
        'sso:access.drawer_view_application_account_user.table_head.column_second.label',
      ),
    },
    {
      label: t(
        'sso:access.drawer_view_application_account_user.table_head.column_third.label',
      ),
    },
  ]

  const APPLICATION_ACCOUNT_GROUPED_BY_APPLICATION = Object.values(
    userAccess?.ApplicationAccountUsers.reduce(
      (acc, app) => {
        const { applicationId, Application, id } = app
        if (!acc[applicationId]) {
          acc[applicationId] = {
            id,
            application: Application,
            users: [],
          }
        }
        acc[applicationId].users.push(app)
        return acc
      },
      {} as Record<
        string,
        {
          id: string
          application: ApplicationModel
          users: UserApplicationAccountUserPaginated['ApplicationAccountUsers']
        }
      >,
    ) || {},
  )

  return (
    <Drawer.Root id="drawer-view-application-account-user">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('sso:access.drawer_view_application_account_user.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {t('sso:access.drawer_view_application_account_user.body.title')}
        </h2>

        <div className="flex items-center gap-2 border border-neutral-200 rounded-2xl py-4 px-5">
          <Avatar
            size="lg"
            type="solid"
            color={colorAvatarByName(
              userAccess?.UserPreference.displayName || '',
            )}
            name={userAccess?.UserPreference.displayName || ''}
            src={userAccess?.UserPreference.avatarUrl || ''}
          />

          <div>
            <p className="font-medium text-sm text-base-800">
              {userAccess?.UserPreference.displayName || ''}
            </p>
            <p className="text-sm font-light text-base-700">
              {userAccess?.email || ''}
            </p>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-brand-700">
          {t('sso:access.drawer_view_application_account_user.body.subtitle')}
        </h2>

        {APPLICATION_ACCOUNT_GROUPED_BY_APPLICATION.length === 0 && (
          <EmptyState
            title={t(
              'sso:access.drawer_view_application_account_user.body.empty_state.title',
            )}
            description={t(
              'sso:access.drawer_view_application_account_user.body.empty_state.description',
            )}
          />
        )}

        {APPLICATION_ACCOUNT_GROUPED_BY_APPLICATION.map((app) => (
          <div key={app.id} className="flex flex-col justify-center gap-2">
            <p className="font-medium text-base text-neutral-800">
              {app.application.title}
            </p>

            <div className="bg-neutral-50 rounded-xl">
              <Table.Root>
                <Table.Content>
                  <Table.Head
                    hideCheckbox
                    cols={TABLE_COLUMNS}
                    onOrderBy={() => {}}
                  />

                  <Table.Body>
                    {app.users.map((appUser) => (
                      <Table.Row hideCheckbox id={appUser.id} key={appUser.id}>
                        <Table.Data>
                          {appUser?.Account ? appUser.Account.title : '-'}
                        </Table.Data>

                        <Table.Data className="flex items-center gap-2">
                          {appUser?.Account?.country && (
                            <img
                              src={countryCodeToFlag(appUser?.Account?.country)}
                              className="size-6 rounded-full"
                              alt={appUser?.Account?.country}
                            />
                          )}
                          {appUser?.Account?.country
                            ? regionName.of(appUser?.Account?.country)
                            : '-'}
                        </Table.Data>

                        <Table.Data>
                          <Badge.Root>
                            {translateApplicationAccountUserRole(
                              t,
                              appUser.role,
                            )}
                          </Badge.Root>
                        </Table.Data>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Content>
              </Table.Root>
            </div>
          </div>
        ))}
      </Drawer.Body>

      <Drawer.Footer className="flex w-full justify-end">
        <Button.Root
          variant="secondary"
          color="brand"
          onClick={drawerViewApplicationAccountUser.close}
          type="button"
        >
          {t(
            'sso:access.drawer_view_application_account_user.footer.button_close.label',
          )}
        </Button.Root>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
